import { WistorAppCtx } from "../../../hooks/use-app-ctx"
import { RemoteProxy } from "penpal"
import { IfcProgressStage, IFrameApi } from "./iframe-api"
import { SparqlManager } from "../../../helpers/SparqlManager"
import { ProgressUpdate } from "../../../shared/progress"
import { useQuery } from "@tanstack/react-query"
import { IfcToFragConverter } from "./conversion/ifc-to-frag-converter"
import { MutableRefObject, useEffect, useMemo } from "react"
import { useLatest } from "../../../hooks/use-latest"
import {
  Converter,
  ConverterDeps,
  DepsValidationStatus,
  FileInfo,
  validateDeps
} from "./conversion/converter-interface"
import { FragToLdConverter } from "./conversion/frag-to-ld-converter"
import { usePubsub } from "../../../redux/pubsub"
import { IFCViewerV4ConfigOptions } from "./config"

type ConversionsConfig = {
  autoIfcToFragEnabled: boolean
  autoIfcToLdEnabled: boolean
  /**
   * A {@link WistorBackendAppCtx} is actually required for conversion
   * At time of writing this, we did not represent this in the typing,
   * as could not account for "Rules of Hooks" for React within reasonable time.
   */
  appCtx: WistorAppCtx
  iframeApi: RemoteProxy<IFrameApi> | null
  ifcUrls: string[]
  fragUrls: string[]
  sparqlManager: SparqlManager
  widgetConfig: IFCViewerV4ConfigOptions
  progressCallback: (input: ProgressUpdate<IfcProgressStage>) => void
  setUpdateTimestamp: (eventCtx: string) => void
}

type ConversionsStatus = DepsValidationStatus | "disabled"

export function useIfcConversions(conf: ConversionsConfig): ConversionsStatus {
  const [ appVars ] = usePubsub()
  const deps: MutableRefObject<ConverterDeps> = useLatest({
    appCtx: conf.appCtx,
    iframeApi: conf.iframeApi,
    sparqlManager: conf.sparqlManager,
    appVars,
    widgetConfig: conf.widgetConfig
  })
  const anyEnabled = conf.autoIfcToFragEnabled || conf.autoIfcToLdEnabled
  const conversionsStatus = anyEnabled
    ? validateDeps(deps.current)
    : "disabled"

  const [ ifcToFragConverter, fragToLdConverter ] = useMemo(() => [
    new IfcToFragConverter(deps),
    new FragToLdConverter(deps)
  ], [ deps ])

  const ifcsWithoutFragsResult = useCandidatesQuery({
    enabled: conf.autoIfcToFragEnabled && conf.ifcUrls.length > 0,
    urls: conf.ifcUrls,
    conf,
    converter: ifcToFragConverter
  })

  const allUrls = [ ...conf.ifcUrls, ...conf.fragUrls ]
  const fragsWithoutGraphResult = useCandidatesQuery({
    enabled: conf.autoIfcToLdEnabled && allUrls.length > 0,
    urls: allUrls,
    conf,
    converter: fragToLdConverter
  })

  useAutoConvert({
    enabled: conf.autoIfcToFragEnabled,
    candidates: ifcsWithoutFragsResult.data,
    conf,
    converter: ifcToFragConverter,
    onConverted: async function invalidateIfcCandidatesAndRefetchFragCandidates() {
      await IfcToFragConverter.invalidate()
      await FragToLdConverter.refetch()
    }
  })
  useAutoConvert({
    enabled: conf.autoIfcToLdEnabled,
    candidates: fragsWithoutGraphResult.data,
    conf,
    converter: fragToLdConverter,
    onConverted: async function invalidateFragCandidates() {
      await FragToLdConverter.invalidate()
    }
  })

  return conversionsStatus
}

function useCandidatesQuery(params: {
  enabled: boolean
  urls: string[]
  converter: Converter
  conf: ConversionsConfig
}) {
  const { enabled, urls, converter, conf } = params
  return useQuery({
    enabled: enabled && urls.length > 0,
    queryKey: [ converter.queryKey, ...urls ],
    queryFn: () => {
      if (conf.appCtx.type !== "wistor-backend") {
        console.warn("Cannot convert IFCs without a wistor backend")
        return
      }
      return converter.findConversionCandidates(urls)
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

function useAutoConvert(params: {
  enabled: boolean
  candidates: FileInfo[] | undefined
  conf: ConversionsConfig
  converter: Converter
  onConverted?: () => void
}) {
  const latest = useLatest({
    conf: params.conf,
    converter: params.converter,
    onConverted: params.onConverted
  })

  useEffect(() => {
    if (!params.candidates || !params.candidates.length) return
    if (!params.enabled) return

    const conf = latest.current.conf
    if (!conf.iframeApi) return
    if (conf.appCtx.type !== "wistor-backend") {
      console.warn("Cannot convert IFCs without a wistor backend")
      return
    }

    const convert = async () => {
      if (!params.candidates || !params.candidates.length) return // make ts happy

      try {
        await Promise.all(params.candidates.map((candidate) => {
          if (!conf.iframeApi) return null // again to make TS happy
          if (conf.appCtx.type !== "wistor-backend") return null // again to make TS happy

          return latest.current.converter.convert(candidate, conf.progressCallback)
        }))
      } finally {
        latest.current.onConverted?.()
      }
    }

    convert()
      .catch(e => console.error(`Failed to convert IFCs`, params.candidates, e))
  }, [
    params.enabled,
    params.candidates,
    latest
  ])
}
